import { FC, useMemo } from "react";
import { Syndicat } from "../../lib/interfaces/syndicat.interface";
import { Building2, ExternalLink, Mail, Phone, UserRound } from "lucide-react";
import logoSna from "../../assets/img/logo-sna-transparent.png";

interface Props {
  syndicat: Syndicat;
}

const MonSyndicat: FC<Props> = ({ syndicat }) => {
  const urlWebsite: string = useMemo(() => {
    if (!!syndicat.site) {
      let url = syndicat.site.trim();
      if (url.startsWith("https://")) return url;
      if (url.startsWith("http://")) return url.replace("http://", "https://");
      return "https://" + url;
    }
    return "";
  }, [syndicat]);
  const contactName = [syndicat.prenomContact, syndicat.nomContact].filter(Boolean).join(" ");
  const hasContactDetails = !!syndicat.telephone || !!syndicat.email || !!urlWebsite || !!contactName;

  return (
    <section className='overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_18px_55px_-35px_rgba(15,23,42,0.45)]'>
      <div className='flex min-h-20 items-center justify-between gap-5 border-b border-slate-100 bg-[#fbfaf6] px-5 py-4 sm:px-7 lg:px-8'>
        <div className='flex min-w-0 items-center gap-3'>
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f1e8c9] text-[#755c14]'>
            <Building2 className='h-5 w-5' />
          </div>
          <div>
            <p className='font-bold text-slate-950'>Votre structure départementale</p>
          </div>
        </div>
        <img
          className='hidden h-12 w-auto object-contain sm:block'
          src={logoSna}
          alt="Syndicat national d'apiculture"
        />
      </div>

      <div className='grid gap-7 p-5 sm:p-7 lg:grid-cols-[minmax(0,1fr)_19rem] lg:gap-10 lg:p-8'>
        <div className='min-w-0'>
          <h2 className='max-w-3xl text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl'>{syndicat.nom}</h2>

          <div className='mt-7 space-y-3 border-t border-slate-100 pt-6'>
            {contactName && (
              <div className='flex items-start gap-4 rounded-xl p-2 sm:p-3'>
                <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f6f1df] text-[#806514]'>
                  <UserRound className='h-5 w-5' />
                </div>
                <div className='min-w-0 pt-0.5'>
                  <p className='text-sm text-slate-500'>Contact</p>
                  <p className='mt-0.5 font-semibold text-slate-900'>{contactName}</p>
                </div>
              </div>
            )}

            {syndicat.telephone && (
              <div className='flex items-start gap-4 rounded-xl p-2 sm:p-3'>
                <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eef5f1] text-[#255b4b]'>
                  <Phone className='h-5 w-5' />
                </div>
                <div className='min-w-0 pt-0.5'>
                  <p className='text-sm text-slate-500'>Téléphone</p>
                  <a
                    className='mt-0.5 block font-semibold text-slate-900 underline-offset-4 hover:text-[#255b4b] hover:underline'
                    href={`tel:${syndicat.telephone.replace(/\s/g, "")}`}
                  >
                    {syndicat.telephone}
                  </a>
                </div>
              </div>
            )}

            {syndicat.email && (
              <div className='flex items-start gap-4 rounded-xl p-2 sm:p-3'>
                <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eef5f1] text-[#255b4b]'>
                  <Mail className='h-5 w-5' />
                </div>
                <div className='min-w-0 pt-0.5'>
                  <p className='text-sm text-slate-500'>Email</p>
                  <a
                    className='mt-0.5 block break-all font-semibold text-slate-900 underline-offset-4 hover:text-[#255b4b] hover:underline'
                    href={`mailto:${syndicat.email}`}
                  >
                    {syndicat.email}
                  </a>
                </div>
              </div>
            )}

            {urlWebsite && (
              <div className='flex items-start gap-4 rounded-xl p-2 sm:p-3'>
                <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f6f1df] text-[#806514]'>
                  <ExternalLink className='h-5 w-5' />
                </div>
                <div className='min-w-0 pt-0.5'>
                  <p className='text-sm text-slate-500'>Site internet</p>
                  <a
                    className='mt-0.5 block break-all font-semibold text-slate-900 underline-offset-4 hover:text-[#806514] hover:underline'
                    href={urlWebsite}
                    target='_blank'
                    rel='noreferrer'
                  >
                    {syndicat.site}
                  </a>
                </div>
              </div>
            )}

            {!hasContactDetails && (
              <p className='rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600'>
                Coordonnées non renseignées. Contactez le SNA pour joindre votre syndicat.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MonSyndicat;
